import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { jobs } from 'actions/jobs';
import { jobsPayloadType } from 'type/JobsType';
import { BASE_URL } from 'constants/routes';
import api from 'api';
import { apiHeaderOptions } from 'api/apiHeaderOptions';
import { BUCKET_NAME } from 'constants/constants';
import { serviceUrl } from 'api/config';

export const useJobsTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // State
  const [paginationValues, setPaginationValues] = useState({
    pageNumber: 1,
    rpp: 10
  });
  const { pageNumber, rpp } = paginationValues;
  const pageSize = 10;
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedJob, setSelectedJob] = useState<string[]>([]);
  const [alert,setAlert] = useState({ open: false, message: '' });
  const [loading, setLoading] = useState(false);

  const [sorting, setSorting] = useState<{
    column: string;
    direction: 'asc' | 'desc' | '';
  }>({
    column: '',
    direction: ''
  });
  const [refresh, setRefresh] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [filteredResults, setFilteredResults] = useState({
    records: [] as any[],
    totalRecords: 0
  });

  // Selectors
  const { userName } = useSelector((state: any) => state.userTypeReducer?.userType?.data) || {};
  const jobsData = useSelector((state: any) => state?.jobsReducer?.jobs);
  const isLoading = useSelector((state: any) => state.jobsReducer.loading);
  const error = useSelector((state: any) => state.jobsReducer.error);

  // Update filtered results when jobsData changes
  useEffect(() => {
    if (jobsData?.list?.length > 0) {
      setFilteredResults({
        records: jobsData.list,
        totalRecords: jobsData.totalCount
      });
    } else {
      setFilteredResults({ records: [], totalRecords: 0 });
    }
  }, [jobsData]);

  // Fetch jobs
  const fetchUserJobs = async () => {
    const payload: jobsPayloadType = {
      userId: userName,
      pageNumber: pageNumber || 1,
      sortType: sorting.direction,
      sortColumn: sorting.column,
      rpp: rpp,
      searchKeyword: searchInput
    };
    dispatch(jobs.fetchJobs(payload) as any);
  };

  // Fetch jobs when dependencies change
  useEffect(() => {
    fetchUserJobs();
  }, [pageNumber, pageSize, rpp, sorting, dispatch, userName, refresh]);

  // Handlers
  const searchHandler = () => {
    if( searchInput.length < 3 ) {
      setAlert({
        open: true,
        message: 'Please enter at least 3 characters to search.'
      });
      return;
    }
    
    setSelectedJob([]);
    setSelectedStatus([]);
    fetchUserJobs();
  };

  const handleRefreshClick = () => {
    setSorting({ column: '', direction: '' });
    setRefresh(!refresh);
  };

  const handleCellClick = (value: any, accessor: any, row: any) => {
    window.scrollTo(0, 0);
    navigate(`${BASE_URL}/job-details/${row.bulkUploadId}`);
  };

  const handleDownloadTemplate = async (path: string) => {
    const fileName = path.split('/').pop() || 'Error.xlsx';
    let bucketName = BUCKET_NAME.prod;
    if (serviceUrl.includes('qa4')) {
      bucketName = BUCKET_NAME.qa4;
    } else if (serviceUrl.includes('qa1')) {
      bucketName = BUCKET_NAME.qa1;
    }
    try {
      setLoading(true);
      const signedUrlApi = `${api.config.getSignedUrl(path)}&bucketName=${bucketName}`;
      const apiHeader = apiHeaderOptions();
      const response = await fetch(signedUrlApi, apiHeader);
      if (!response.ok) {
        console.error('Error fetching signed URL:', response.status, fileName);
        return false;
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const fileResponse = await fetch(data.success.preSignedURL);
      if (!fileResponse.ok) {
        throw new Error('Failed to fetch the file');
      }
      const blob = await fileResponse.blob();
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName;
      setLoading(false);
      link.click();
      URL.revokeObjectURL(url);
      return true;
    } catch (err: any) {
      setLoading(false);
      console.error('Error fetching presigned URL:', err);
      return false;
    }
  };

  const handleSortingChange = (column: string, direction: 'asc' | 'desc' | '') => {
    setPaginationValues({ ...paginationValues, pageNumber: 1 });
    setSorting({ column, direction });
  };

  const handleActivePageChange = (page: any) => {
    window.scrollTo(0, 0);
    setPaginationValues({ ...paginationValues, pageNumber: page });
  };

  const searchItems = (searchValue: string) => {
    window.scrollTo(0, 0);
    setSearchInput(searchValue);
    if(searchValue.trim().length === 0){
      handleClearSearch();
    }
  };

  const handleResultsPerPageChange = (size: any) => {
    window.scrollTo(0, 0);
    setPaginationValues({ ...paginationValues, pageNumber: 1, rpp: size });
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setPaginationValues({ ...paginationValues, pageNumber: 1, rpp: 10 });
    setSelectedJob([]);
    setSelectedStatus([]);
    const payload: jobsPayloadType = {
      userId: userName,
      pageNumber: 1,
      sortType: sorting.direction,
      sortColumn: sorting.column,
      rpp: 10,
      searchKeyword: ''
    };
    dispatch(jobs.fetchJobs(payload) as any);
  };

  // Apply filters based on selected job and status
  const applyFiltersAndSearch = useCallback(() => {
    if (!jobsData?.list) {
      setFilteredResults({ records: [], totalRecords: 0 });
      return;
    }

    let filteredData = [...jobsData.list];

    // Apply search filter first
    if (searchInput && searchInput.length > 2) {
      filteredData = filteredData.filter((job: any) => {
        return (
          job?.jobName?.toLowerCase().includes(searchInput.toLowerCase()) ||
          job?.fileName?.toLowerCase().includes(searchInput.toLowerCase()) ||
          job?.status?.toLowerCase().includes(searchInput.toLowerCase()) ||
          job?.bulkUploadId
            ?.toString()
            ?.toLowerCase()
            .includes(searchInput.toLowerCase())
        );
      });
    }

    // Apply status filter
    if (selectedStatus.length > 0) {
      filteredData = filteredData.filter((job: any) => 
        selectedStatus.includes(job.status)
      );
    }

    // Apply job name filter
    if (selectedJob.length > 0) {
      filteredData = filteredData.filter((job: any) => 
        selectedJob.includes(job.jobName)
      );
    }

    const mappedData = filteredData.map((job: any) => ({
      jobName: job.jobName,
      bulkUploadId: job.bulkUploadId,
      fileName: job.fileName,
      uploadedDate: job.uploadedDate,
      completedDate: job.completedDate,
      status: job.status,
      assetCount: job.assetCount,
      errorMessage: job.errorMessage,
      errorFilePath: job.errorFilePath,
      jobsConsolidatedStatus: job.jobsConsolidatedStatus // Preserve this property for clickability
    }));

    setFilteredResults({
      records: mappedData,
      totalRecords: jobsData.totalCount
    });
  }, [selectedJob, selectedStatus]);

  // Apply filters when dependencies change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [applyFiltersAndSearch]);

  // Generate unique statuses and jobs
  const uniqueStatuses = Array.from(
    new Set(
      jobsData?.list
        ?.map((job: any) => job.status?.toLowerCase())
        .filter(Boolean) || []
    )
  ).map(status => {
    const originalStatus = jobsData?.list?.find(
      (job: any) => job.status?.toLowerCase() === status
    )?.status;
    return originalStatus || status;
  }) as string[];
  
  const uniqueJobs = Array.from(
    new Set(jobsData?.list?.map((job: any) => job.jobName).filter(Boolean) || [])
  ) as string[];

  const uniqueStatuseswithLabels = uniqueStatuses.map((status: string) => ({
    value: status,
    label: status?.charAt(0)?.toUpperCase() + status?.slice(1)?.toLowerCase().replaceAll('_', ' ')
  }));

  const uniqueJobswithLabels = uniqueJobs.map((job: string) => ({
    value: job,
    label: job
  }));

  // Generate page size options
  const generatePageSizeOptions = (totalRecords: number) => {
    const options = [5, 10];
    if (totalRecords > 10) {
      options.push(20);
    }
    if (totalRecords > 20) {
      options.push(30);
    }
    if (totalRecords > 30) {
      options.push(50);
    }
    const filteredOptions = options.filter(option => option <= totalRecords || option === 10);
    return filteredOptions.length > 0 ? filteredOptions : [10];
  };

  const rppList = generatePageSizeOptions(filteredResults.totalRecords);

  return {
    // State
    paginationValues,
    pageNumber,
    rpp,
    pageSize,
    selectedStatus,
    setSelectedStatus,
    selectedJob,
    setSelectedJob,
    sorting,
    searchInput,
    filteredResults,
    isLoading,
    error,
    uniqueStatuseswithLabels,
    uniqueJobswithLabels,
    rppList,
    alert,
    setAlert,
    
    // Handlers
    searchHandler,
    handleRefreshClick,
    handleCellClick,
    handleDownloadTemplate,
    handleSortingChange,
    handleActivePageChange,
    searchItems,
    handleResultsPerPageChange,
    handleClearSearch,
    loading
  };
};
